import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ColorNeutralGrey2, Spacing2, Spacing6, Spacing8, Spacing16, Spacing24 } from 'autogen/design-tokens/tokens';
import type { PriceFilterViewModel } from 'autogen/swagger/all';
import { PriceIntervalClear, PriceIntervalMax, PriceIntervalMin } from 'autogen/translation-keys/trans-website-filter';
import InlineButton from 'components/generic/InlineButton';
import { Typography } from 'components/generic/typography';
import { TextInput } from 'components/shared/inputs/text';
import useGA4Tracking from 'modules/tracking/GA4';
import useFilters from '../../../shared/hooks/useFilters';
import FilterMenuDrawer from '../../FilterMenu/FilterMenuDrawer';
import useFilterMenu from '../../FilterMenu/shared/hooks/useFilterMenu';
import BooleanFilterOption from '../BooleanFilter/BooleanFilterOption';

const MinMaxWrapper = styled.div<{ $showClearButton: boolean; $disabled: boolean }>`
    display: grid;
    gap: ${Spacing8};
    grid-template-columns: repeat(2, calc(50% - ${Spacing8} / 2));
    padding-bottom: ${Spacing24};

    ${({ $showClearButton }) =>
        $showClearButton &&
        css`
            padding: 0;
            grid-template-areas:
                "minLabel maxLabel"
                "minInput maxInput"
                ". clearInput";
        `};


    ${({ $disabled }) =>
        $disabled &&
        css`
            opacity: 0.4;
            cursor: not-allowed;

            & > * {
                pointer-events: none;
            }
    `};
`;

const Divider = styled.div`
    background: ${ColorNeutralGrey2};
    height: 1px;
    width: 100%;
    margin: ${Spacing16} 0;
`;

const StyledInlineButton = styled(InlineButton)`
    grid-area: clearInput;
    justify-self: end;
    margin: -${Spacing2} 0 -${Spacing6};
`;

const PriceWrapper = styled.div<{ $disabled: boolean }>`
    ${({ $disabled }) =>
        $disabled &&
        css`
            opacity: 0.4;
            cursor: not-allowed;

            & > * {
                pointer-events: none;
            }
        `};
`;

const PriceFilterMenu = ({
    model: {
        displayName,
        priceInterval,
        prices,
        attribute: { id }
    }
}: {
    model: PriceFilterViewModel;
}) => {
    const filterMenuId = id.toString();
    const { closeFilterMenu } = useFilterMenu(filterMenuId);
    const { minValue, maxValue, queryStringName: intervalQueryStringName } = priceInterval;
    const { options, queryStringName: boolQueryStringName } = prices;
    const [minimumValue, setMinimumValue] = useState<number | string | undefined>(minValue);
    const [maximumValue, setMaximumValue] = useState<number | string | undefined>(maxValue);
    const { removePendingFilter, isPendingFilterActive, updatePendingFilter } = useFilters();
    const ga4Tracking = useGA4Tracking();
    const { t } = useTranslation();
    const isIntervalUsed = isPendingFilterActive(intervalQueryStringName);

    function handleClearInterval() {
        removePendingFilter(intervalQueryStringName);
        setMinimumValue('');
        setMaximumValue('');
    }

    function handleOnBlurMin(minimumInputValue: number) {
        let nextMin = minimumInputValue;

        if (maximumValue && Number(maximumValue) <= minimumInputValue) {
            nextMin = Number(maximumValue) - 1;
            setMinimumValue(nextMin);
        }

        updatePendingFilter(intervalQueryStringName, `${nextMin}-${maximumValue}`);
    }

    function handleOnBlurMax(maximumInputValue: number) {
        let nextMax = maximumInputValue;

        if (minimumValue && Number(minimumValue) >= maximumInputValue) {
            nextMax = Number(minimumValue) + 1;
            setMaximumValue(nextMax);
        }

        updatePendingFilter(intervalQueryStringName, `${minimumValue}-${nextMax}`);
    }

    function handleOnBlur(value, type: 'min' | 'max') {
        if ((minimumValue || minimumValue === 0) && maximumValue) {
            if (type === 'min') handleOnBlurMin(value);
            if (type === 'max') handleOnBlurMax(value);

            ga4Tracking({
                eventName: 'select_filter',
                category: 'ecommerce',
                eventParams: [
                    { param_name: 'list_name', param_value: displayName },
                    { param_name: 'filter_name', param_value: `${minimumValue}-${maximumValue}` },
                    { param_name: 'filter_list_name', param_value: 'PrisInterval' }
                ]
            });
        }

        if (!minimumValue && minimumValue !== 0 && !maximumValue && maximumValue !== 0) {
            handleClearInterval();
        }
    }

    // Used to clear input fields if minValue or maxValue return null in BE
    // biome-ignore lint/correctness/useExhaustiveDependencies: handleClearInterval is not needed as dependency
    useEffect(() => {
        if (maxValue === null || minValue === null) {
            handleClearInterval();
        }
    }, [minValue, maxValue]);

    return (
        <FilterMenuDrawer id={filterMenuId} headline={displayName} backButtonCallback={closeFilterMenu}>
            <MinMaxWrapper $showClearButton={isIntervalUsed} $disabled={prices.isActive}>
                <Typography.Body size="M" strong as="span">
                    {t(PriceIntervalMin)}
                </Typography.Body>
                <Typography.Body size="M" strong as="span">
                    {t(PriceIntervalMax)}
                </Typography.Body>
                <TextInput
                    value={minimumValue}
                    onChange={(event) => {
                        setMinimumValue(event.target.valueAsNumber);
                    }}
                    onBlur={(event) => {
                        handleOnBlur(event.target.valueAsNumber, 'min');
                    }}
                    type="number"
                    max={maximumValue}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleOnBlur(e.currentTarget.valueAsNumber, 'min');
                        }
                    }}
                    min={0}
                />
                <TextInput
                    value={maximumValue}
                    onChange={(event) => {
                        setMaximumValue(event.target.valueAsNumber);
                    }}
                    onBlur={(event) => handleOnBlur(event.target.valueAsNumber, 'max')}
                    type="number"
                    min={minimumValue || 0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleOnBlur(e.currentTarget.valueAsNumber, 'max');
                        }
                    }}
                />
                {isIntervalUsed && (
                    <StyledInlineButton fontWeight="semibold" size="L" onClick={handleClearInterval}>
                        {t(PriceIntervalClear)}
                    </StyledInlineButton>
                )}
            </MinMaxWrapper>
            <Divider />
            <PriceWrapper $disabled={isIntervalUsed}>
                {options.map((option) => (
                    <BooleanFilterOption
                        key={option.displayName}
                        model={option}
                        queryStringName={boolQueryStringName}
                        menuDisplayName={displayName}
                    />
                ))}
            </PriceWrapper>
        </FilterMenuDrawer>
    );
};

export default memo(PriceFilterMenu);
